import { X } from 'lucide-react';
import { useTranslation } from '@/i18n/hooks';
import type { CategoryType } from '../useCatalog';

interface CategoryFilterProps {
  selectedCategory: CategoryType;
  onCategoryChange: (category: CategoryType) => void;
  showFilters: boolean;
  onClose: () => void;
}

export function CategoryFilter({
  selectedCategory,
  onCategoryChange,
  showFilters,
  onClose,
}: CategoryFilterProps) {
  const { t } = useTranslation('catalog');
  const { t: tCommon } = useTranslation('common');

  const categories = [
    { value: 'all' as const, label: t('categories.all') },
    { value: 'new' as const, label: t('categories.new') },
    { value: 'used' as const, label: t('categories.used') },
    { value: 'buffalo' as const, label: t('categories.buffalo') },
    { value: 'power-citros' as const, label: t('categories.power-citros') },
    { value: 'parts' as const, label: t('categories.parts') },
  ] as const;

  return (
    <>
      {/* Mobile Overlay */}
      {showFilters && (
        <button
          type="button"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          aria-label={tCommon('actions.close')}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 z-50 h-full w-80 overflow-y-auto border-r border-slate-200 bg-white p-6 transition-transform duration-300 dark:border-slate-700 dark:bg-slate-800 lg:static lg:z-auto lg:h-auto lg:w-72 lg:border-0 ${
          showFilters ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Mobile Close Button */}
        <div className="mb-6 flex items-center justify-between lg:hidden">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('filterBy')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
            aria-label={tCommon('actions.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Categories Filter */}
        <div className="mb-8">
          <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('categories.title')}
          </h3>

          <div className="space-y-2">
            {categories.map((category) => (
              <button
                key={category.value}
                type="button"
                onClick={() => onCategoryChange(category.value)}
                className={`w-full rounded-lg px-4 py-3 text-left transition-all ${
                  selectedCategory === category.value
                    ? 'border border-secondary bg-secondary/10 font-medium text-secondary dark:bg-secondary/20'
                    : 'border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700'
                }`}
                aria-pressed={selectedCategory === category.value}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* Clear Filters */}
        {selectedCategory !== 'all' && (
          <button
            type="button"
            onClick={() => onCategoryChange('all')}
            className="w-full rounded-lg bg-red-100 px-4 py-3 font-medium text-red-700 transition-colors hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
            aria-label={t('clearFilters')}
          >
            {t('clearFilters')}
          </button>
        )}
      </div>
    </>
  );
}
